/**
 * Predimensionamiento de vigas — peralte por luz libre.
 *
 *   h ≈ L / k         (L en cm, k según tipo de viga)
 *   b ≈ h / 2         (mínimo 25 cm para vigas principales, 13 cm para soleras)
 *
 * k típicos (para vigas continuas):
 *   peraltada (principal):  L / 12
 *   chata (placa):          L / 20    — se aloja en el espesor de la losa
 *   solera (albañilería):   L / 14    — encima del muro confinado
 *   invertida:              L / 12    — sobre la losa, igual que peraltada
 *   arriostre (cimentación):L / 14
 */

import { useState } from 'react';
import { PredimCard, Group, NumberRow, ResultRow, FormulaNote, roundTo } from './shared';
import { BeamSectionDiagram, BeamElevationDiagram, DiagramPanel, View2D3DToggle } from './diagrams';
import { Beam3D } from './views3d';
import { PredimToolbar } from './PredimToolbar';
import type { DesignFull } from '@/features/designs/types';

type Variant = 'peraltada' | 'chata' | 'solera' | 'invertida' | 'arriostre';

interface PredimBeamProps {
  variant: Variant;
  initialInputs?: {
    L_beam?: number;
    continuity?: 'simple' | 'continuous' | 'cantilever';
  } | null;
  existing?: DesignFull | null;
}

const VARIANT_PRESETS: Record<Variant, { k: number; bMin: number; label: string; descr: string }> = {
  peraltada: {
    k: 12, bMin: 25,
    label: 'Viga peraltada (principal)',
    descr: 'Viga principal de pórtico. h ≈ L/12, b ≈ h/2. Mínimo b = 25 cm.',
  },
  chata: {
    k: 20, bMin: 25,
    label: 'Viga chata (dentro de losa)',
    descr: 'Embebida en el espesor de la losa. h ≈ L/20. Usa para luces cortas.',
  },
  solera: {
    k: 14, bMin: 13,
    label: 'Viga solera (albañilería)',
    descr: 'Encima del muro confinado. h = espesor del muro (mínimo 20 cm) o L/14.',
  },
  invertida: {
    k: 12, bMin: 25,
    label: 'Viga invertida',
    descr: 'Cuelga sobre la losa. h ≈ L/12 igual que peraltada.',
  },
  arriostre: {
    k: 14, bMin: 25,
    label: 'Viga de arriostre (cimentación)',
    descr: 'Une cimentaciones contra desplazamiento horizontal. h ≈ L/14.',
  },
};

export function PredimBeam({ variant, initialInputs, existing }: PredimBeamProps) {
  const preset = VARIANT_PRESETS[variant];
  const [L, setL] = useState(initialInputs?.L_beam ?? 500);
  const [continuity, setContinuity] = useState<'simple' | 'continuous' | 'cantilever'>(
    initialInputs?.continuity ?? 'continuous',
  );

  // Adjust the k factor by continuity
  const k =
    continuity === 'simple' ? preset.k * 0.85          // simple support → slightly deeper
    : continuity === 'cantilever' ? preset.k / 2       // cantilever → roughly L/6 for peraltada
    : preset.k;                                          // continuous → base value

  const h_required = L / k;
  const h = roundTo(h_required, 5);
  const b_min = Math.max(preset.bMin, roundTo(h / 2, 5));
  const b = b_min;

  const exportInputs = { beam_variant: variant, L_beam: L, continuity };
  const [view, setView] = useState<'2D' | '3D'>('2D');

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex justify-end">
        <PredimToolbar
          family="beams"
          variant={variant}
          inputs={exportInputs}
          existing={existing}
        />
      </div>
      <PredimCard title={preset.label} subtitle={preset.descr}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Group title="Geometría">
            <NumberRow label="Luz libre L" value={L} onChange={setL} step={25} unit="cm" />
            <label className="flex items-center justify-between gap-3 text-[12px]">
              <span style={{ color: 'var(--text-secondary)' }}>Condiciones de apoyo</span>
              <select
                value={continuity}
                onChange={(e) => setContinuity(e.target.value as typeof continuity)}
                className="w-[180px] rounded-[var(--radius-sm)] border px-2 py-1 text-[12px]"
                style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              >
                <option value="continuous">Continua (típico de pórtico)</option>
                <option value="simple">Simplemente apoyada</option>
                <option value="cantilever">Voladizo</option>
              </select>
            </label>
          </Group>
          <Group title="Criterio">
            <div className="text-[12px] space-y-1 font-mono" style={{ color: 'var(--text-secondary)' }}>
              <div>h = L / {k.toFixed(1)}</div>
              <div>b = max(h/2, {preset.bMin} cm)</div>
            </div>
          </Group>
        </div>

        <FormulaNote>
          Predimensionado por relación luz/peralte. Para condiciones especiales (cargas
          concentradas grandes, deflexiones críticas) verifica con ACI 318 9.5 / E.060 9.6.2.
        </FormulaNote>
      </PredimCard>

      <PredimCard title="Resultados">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-1">
            <ResultRow label="h teórico (L/k)" value={`${h_required.toFixed(1)} cm`} />
            <ResultRow label="h redondeado" value={`${h} cm`} highlight />
            <ResultRow label="b mínimo recomendado" value={`${b} cm`} highlight />
            <ResultRow label="Relación final h / L" value={`1 / ${(L / h).toFixed(1)}`} />
          </div>
          <DiagramPanel title="Sección transversal">
            <BeamSectionDiagram b={b} h={h} />
          </DiagramPanel>
        </div>
        <div className="mt-3">
          <DiagramPanel
            title={view === '3D' ? 'Vista 3D' : 'Elevación'}
            toolbar={<View2D3DToggle value={view} onChange={setView} />}
          >
            {view === '3D' ? (
              <div style={{ width: 540, height: 320 }}>
                <Beam3D b={b} h={h} L={L} />
              </div>
            ) : (
              <BeamElevationDiagram L={L} h={h} continuity={continuity} />
            )}
          </DiagramPanel>
        </div>

        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Para diseño detallado por flexión / cortante / deflexión utiliza el módulo <strong>Vigas</strong>
          con cargas factoradas reales.
        </div>
      </PredimCard>
    </div>
  );
}
